/**
 * Shared Zod helpers for form schemas that interact with HTML `<input>`
 * elements and optional backend DTO fields.
 *
 * HTML inputs register with `value=""` when unset, but backend
 * `@IsOptional()` decorators only skip validation on `undefined`. The
 * "optional*" helpers below pipe `""` → `undefined` so an empty input never
 * trips `@Length`, `@IsEmail`, `@IsDateString`, `@IsUUID` on a field the
 * API considers optional.
 *
 * Lives in `@axon/common` so both tiers (frontend RHF forms + backend
 * zod-based validators where present) share the same definitions.
 */

import { z } from 'zod';

export const zodHelpers = {
  /**
   * Optional trimmed string. `""` → `undefined`. Use for every optional
   * text field; backend `@IsOptional() @Length(min>0, max)` will then
   * accept the missing value correctly.
   */
  optionalString: () =>
    z
      .string()
      .trim()
      .transform((val) => (val === '' ? undefined : val))
      .optional(),

  /**
   * Optional email — pipes the empty-to-undefined transform through
   * `z.string().email()` so `""` no longer fails `IsEmail`.
   */
  optionalEmail: () => zodHelpers.optionalString().pipe(z.string().email().optional()),

  /**
   * Optional UUID — same pattern as `optionalEmail`, gates on
   * `z.string().uuid()` after the empty-to-undefined transform.
   */
  optionalUuid: () => zodHelpers.optionalString().pipe(z.string().uuid().optional()),

  /**
   * Optional ISO date string. `<Input type="date">` emits `yyyy-mm-dd`
   * when set and `""` when unset — the optional-string transform pipes the
   * empty case to `undefined` so backend `@IsOptional() @IsDateString()`
   * accepts it. Format is not re-validated on the client since the native
   * date input already guarantees it; server-side `IsDateString` is the
   * authoritative check.
   */
  optionalDate: () => zodHelpers.optionalString(),

  /** Nullable trimmed string. `""` → `null`. */
  nullableString: () =>
    z
      .string()
      .trim()
      .transform((val) => (val === '' ? null : val))
      .nullable(),

  /** Non-negative decimal (coerces strings from `<Input type="number">`). */
  nonNegativeNumber: () => z.coerce.number().min(0),

  /** Strictly positive decimal. */
  positiveNumber: () => z.coerce.number().positive(),

  /**
   * Numeric string → number. Throws on non-numeric. Kept for legacy
   * callers; prefer `z.coerce.number()` in new code.
   */
  numberString: () =>
    z.string().transform((val) => {
      const num = parseFloat(val);
      if (isNaN(num)) throw new Error('Invalid number');
      return num;
    }),

  /** Date string → Date object. Kept for legacy callers. */
  dateString: () =>
    z.string().transform((val) => {
      const date = new Date(val);
      if (isNaN(date.getTime())) throw new Error('Invalid date');
      return date;
    }),

  /** Enum with a per-locale custom error message. */
  localizedEnum: <T extends string>(
    values: readonly T[] & [T, ...T[]],
    fieldName: string,
    errorTemplate: (field: string, vals: readonly string[]) => string = (f, v) =>
      `${f} must be one of: ${v.join(', ')}`
  ) =>
    z.enum(values, {
      message: errorTemplate(fieldName, values as readonly string[]),
    }),
};
